/**
 * MembershipDetailPage — /admin/memberships/:id
 *
 * Shows membership info card, action buttons, renewal history, and
 * linked invoices.
 */

import React, { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import styled from 'styled-components';

import { PageContainer } from '@/components/layout';
import { Badge, Button, Card, Input, Dropdown, Modal } from '@/components/common';
import {
  useMembership,
  useMembershipRenewals,
  usePauseMembership,
  useResumeMembership,
  useSkipNextMembership,
  useCancelMembership,
} from '@features/memberships/api/hooks';
import { useMembershipsConfig } from '@features/memberships/api/config';
import { PAUSE_REASON_PRESETS, CANCEL_REASON_PRESETS } from '@features/memberships/config/ui';
import { formatPaise } from '@features/catalog/config/ui';
import type { MembershipStatus, MembershipRenewal } from '@features/memberships/api/client';

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const InfoField = styled.div``;

const InfoLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
`;

const InfoValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  color: ${({ theme }) => theme.colors.text};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
`;

const ActionsBar = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  padding: ${({ theme }) => theme.spacing.md};
  text-align: left;
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const Td = styled.td`
  padding: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const Tr = styled.tr`
  &:last-child td {
    border-bottom: none;
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.lg};
  padding-top: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const ErrorMsg = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.error};
  margin: 2px 0 0;
`;

const Center = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

function statusBadgeVariant(
  status: MembershipStatus,
): 'success' | 'warning' | 'error' | 'default' {
  switch (status) {
    case 'active':    return 'success';
    case 'paused':    return 'warning';
    case 'cancelled': return 'error';
    default:          return 'default';
  }
}

function renewalStatusVariant(
  s: MembershipRenewal['status'],
): 'success' | 'warning' | 'error' | 'default' {
  switch (s) {
    case 'generated': return 'success';
    case 'skipped':   return 'warning';
    case 'failed':    return 'error';
    default:          return 'default';
  }
}

// ---------------------------------------------------------------------------
// Pause dialog
// ---------------------------------------------------------------------------

interface PauseDialogProps {
  isOpen: boolean;
  onClose: () => void;
  membershipId: string;
  minReasonLength: number;
}

const PauseDialog: React.FC<PauseDialogProps> = ({
  isOpen,
  onClose,
  membershipId,
  minReasonLength,
}) => {
  const pauseMut = usePauseMembership();
  const [pausedUntil, setPausedUntil] = useState('');
  const [reason, setReason] = useState('');
  const [reasonPreset, setReasonPreset] = useState('');
  const [err, setErr] = useState('');

  const handleSubmit = async () => {
    const finalReason = reasonPreset !== 'Other' && reasonPreset ? reasonPreset : reason;
    if (finalReason.length < minReasonLength) {
      setErr(`Reason must be at least ${minReasonLength} characters`);
      return;
    }
    if (!pausedUntil) {
      setErr('Pause until date is required');
      return;
    }
    setErr('');
    await pauseMut.mutateAsync({
      id: membershipId,
      body: { paused_until: pausedUntil, reason: finalReason },
    });
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Pause membership"
      size="md"
      data-testid="candeza-memberships-detail-pause-dialog"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Input
          label="Pause until *"
          type="date"
          value={pausedUntil}
          onChange={(e) => setPausedUntil(e.target.value)}
        />
        <Dropdown
          label="Reason preset"
          placeholder="Select reason…"
          value={reasonPreset}
          onChange={(v) => { setReasonPreset(v); if (v !== 'Other') setReason(v); }}
          options={PAUSE_REASON_PRESETS.map((r) => ({ value: r, label: r }))}
        />
        {(reasonPreset === 'Other' || !reasonPreset) && (
          <div>
            <Input
              label={`Reason (min ${minReasonLength} chars) *`}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason…"
            />
          </div>
        )}
        {err && <ErrorMsg>{err}</ErrorMsg>}
      </div>
      <ModalActions>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          data-testid="candeza-memberships-detail-pause-confirm-btn"
          isLoading={pauseMut.isPending}
          onClick={() => void handleSubmit()}
        >
          Pause membership
        </Button>
      </ModalActions>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Cancel dialog
// ---------------------------------------------------------------------------

interface CancelDialogProps {
  isOpen: boolean;
  onClose: () => void;
  membershipId: string;
  minReasonLength: number;
}

const CancelDialog: React.FC<CancelDialogProps> = ({
  isOpen,
  onClose,
  membershipId,
  minReasonLength,
}) => {
  const cancelMut = useCancelMembership();
  const [reason, setReason] = useState('');
  const [reasonPreset, setReasonPreset] = useState('');
  const [err, setErr] = useState('');

  const handleSubmit = async () => {
    const finalReason = reasonPreset !== 'Other' && reasonPreset ? reasonPreset : reason;
    if (finalReason.length < minReasonLength) {
      setErr(`Reason must be at least ${minReasonLength} characters`);
      return;
    }
    setErr('');
    await cancelMut.mutateAsync({ id: membershipId, body: { reason: finalReason } });
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Cancel membership"
      size="md"
      data-testid="candeza-memberships-detail-cancel-dialog"
    >
      <p style={{ marginBottom: 12, color: '#ef4444', fontSize: 13 }}>
        ⚠ This action cannot be undone. Invoices will stop being generated.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Dropdown
          label="Reason preset"
          placeholder="Select reason…"
          value={reasonPreset}
          onChange={(v) => { setReasonPreset(v); if (v !== 'Other') setReason(v); }}
          options={CANCEL_REASON_PRESETS.map((r) => ({ value: r, label: r }))}
        />
        {(reasonPreset === 'Other' || !reasonPreset) && (
          <Input
            label={`Reason (min ${minReasonLength} chars) *`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Enter reason…"
          />
        )}
        {err && <ErrorMsg>{err}</ErrorMsg>}
      </div>
      <ModalActions>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="danger"
          isLoading={cancelMut.isPending}
          onClick={() => void handleSubmit()}
        >
          Confirm cancellation
        </Button>
      </ModalActions>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export const MembershipDetailPage: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: membership, isLoading, isError, refetch } = useMembership(id);
  const { data: renewalsData, isLoading: renewalsLoading } = useMembershipRenewals(id);
  const { rules } = useMembershipsConfig();

  const resumeMut = useResumeMembership();
  const skipMut = useSkipNextMembership();

  const [pauseOpen, setPauseOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [resumeConfirmOpen, setResumeConfirmOpen] = useState(false);
  const [skipConfirmOpen, setSkipConfirmOpen] = useState(false);

  if (isLoading) {
    return (
      <PageContainer title="Membership" data-testid="candeza-memberships-detail-page">
        <Center>Loading…</Center>
      </PageContainer>
    );
  }

  if (isError || !membership) {
    return (
      <PageContainer title="Membership" data-testid="candeza-memberships-detail-page">
        <Center>
          Failed to load membership.{' '}
          <Button size="sm" variant="outline" onClick={() => refetch()}>
            Retry
          </Button>
        </Center>
      </PageContainer>
    );
  }

  const renewals = renewalsData?.items ?? [];

  return (
    <PageContainer
      title={`Membership — ${membership.student_name_snapshot}`}
      subtitle={`${membership.package_name_snapshot} · ${membership.status}`}
      data-testid="candeza-memberships-detail-page"
      actions={
        <Button variant="outline" size="sm" onClick={() => navigate('/admin/memberships')}>
          ← Back
        </Button>
      }
    >
      {/* Info card */}
      <Card padding="lg" style={{ marginBottom: 24 }}>
        <InfoGrid>
          <InfoField>
            <InfoLabel>Student</InfoLabel>
            <InfoValue>{membership.student_name_snapshot}</InfoValue>
          </InfoField>
          <InfoField>
            <InfoLabel>Package</InfoLabel>
            <InfoValue>{membership.package_name_snapshot}</InfoValue>
          </InfoField>
          <InfoField>
            <InfoLabel>Amount</InfoLabel>
            <InfoValue>{formatPaise(membership.amount)}</InfoValue>
          </InfoField>
          <InfoField>
            <InfoLabel>Recurrence</InfoLabel>
            <InfoValue>
              Every {membership.recurrence.interval}{' '}
              {membership.recurrence.unit}(s)
            </InfoValue>
          </InfoField>
          <InfoField>
            <InfoLabel>Next Renewal</InfoLabel>
            <InfoValue>{membership.next_invoice_date}</InfoValue>
          </InfoField>
          <InfoField>
            <InfoLabel>Status</InfoLabel>
            <Badge variant={statusBadgeVariant(membership.status)}>
              {membership.status}
            </Badge>
          </InfoField>
          {membership.paused_until && (
            <InfoField>
              <InfoLabel>Paused Until</InfoLabel>
              <InfoValue>{membership.paused_until}</InfoValue>
            </InfoField>
          )}
          {membership.cancel_reason && (
            <InfoField>
              <InfoLabel>Cancel Reason</InfoLabel>
              <InfoValue>{membership.cancel_reason}</InfoValue>
            </InfoField>
          )}
        </InfoGrid>

        {/* Action buttons */}
        <ActionsBar>
          {membership.status === 'active' && rules.flags.memberships_allow_pause && (
            <Button
              variant="secondary"
              size="sm"
              data-testid="candeza-memberships-detail-pause-btn"
              onClick={() => setPauseOpen(true)}
            >
              Pause
            </Button>
          )}
          {membership.status === 'paused' && (
            <Button
              variant="secondary"
              size="sm"
              data-testid="candeza-memberships-detail-resume-btn"
              onClick={() => setResumeConfirmOpen(true)}
            >
              Resume
            </Button>
          )}
          {membership.status === 'active' && rules.flags.memberships_allow_admin_skip && (
            <Button
              variant="outline"
              size="sm"
              data-testid="candeza-memberships-detail-skip-btn"
              onClick={() => setSkipConfirmOpen(true)}
            >
              Skip next renewal
            </Button>
          )}
          {membership.status !== 'cancelled' && (
            <Button
              variant="danger"
              size="sm"
              data-testid="candeza-memberships-detail-cancel-btn"
              onClick={() => setCancelOpen(true)}
            >
              Cancel
            </Button>
          )}
        </ActionsBar>
      </Card>

      {/* Renewal history */}
      <Card padding="lg">
        <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 16 }}>
          Renewal History
        </h3>
        {renewalsLoading ? (
          <Center>Loading renewals…</Center>
        ) : renewals.length === 0 ? (
          <Center>No renewals yet.</Center>
        ) : (
          <TableWrapper data-testid="candeza-memberships-detail-renewals-table">
            <StyledTable>
              <thead>
                <tr>
                  <Th>Date</Th>
                  <Th>Status</Th>
                  <Th>Amount</Th>
                  <Th>Invoice</Th>
                  <Th>Generated by</Th>
                </tr>
              </thead>
              <tbody>
                {renewals.map((r: MembershipRenewal) => (
                  <Tr
                    key={r.renewal_date}
                    data-testid={`candeza-memberships-detail-renewal-row-${r.renewal_date}`}
                  >
                    <Td>{r.renewal_date}</Td>
                    <Td>
                      <Badge variant={renewalStatusVariant(r.status)}>
                        {r.status}
                      </Badge>
                    </Td>
                    <Td>{formatPaise(r.amount)}</Td>
                    <Td>
                      {r.invoice_id ? (
                        <Link to={`/invoices?id=${r.invoice_id}`}>
                          {r.invoice_number ?? r.invoice_id}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </Td>
                    <Td>{r.generated_by}</Td>
                  </Tr>
                ))}
              </tbody>
            </StyledTable>
          </TableWrapper>
        )}
      </Card>

      {/* Audit timeline — TODO: wire to audit_log when endpoint is available */}
      {/* TODO: add audit timeline here once GET /v1/audit-log?resource=membership&id={id} is shipped */}

      {/* Dialogs */}
      <PauseDialog
        isOpen={pauseOpen}
        onClose={() => setPauseOpen(false)}
        membershipId={id}
        minReasonLength={rules.pause_reason_min_length}
      />

      <CancelDialog
        isOpen={cancelOpen}
        onClose={() => setCancelOpen(false)}
        membershipId={id}
        minReasonLength={rules.cancel_reason_min_length}
      />

      {/* Resume confirm */}
      <Modal
        isOpen={resumeConfirmOpen}
        onClose={() => setResumeConfirmOpen(false)}
        title="Resume membership?"
        size="sm"
      >
        <p>This will resume billing for the next due date.</p>
        <ModalActions>
          <Button variant="outline" onClick={() => setResumeConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            isLoading={resumeMut.isPending}
            onClick={() => {
              resumeMut.mutate(id, { onSuccess: () => setResumeConfirmOpen(false) });
            }}
          >
            Resume
          </Button>
        </ModalActions>
      </Modal>

      {/* Skip confirm */}
      <Modal
        isOpen={skipConfirmOpen}
        onClose={() => setSkipConfirmOpen(false)}
        title="Skip next renewal?"
        size="sm"
      >
        <p>
          No invoice will be generated for the upcoming renewal date. The
          schedule will advance to the next cycle.
        </p>
        <ModalActions>
          <Button variant="outline" onClick={() => setSkipConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            isLoading={skipMut.isPending}
            onClick={() => {
              skipMut.mutate(id, { onSuccess: () => setSkipConfirmOpen(false) });
            }}
          >
            Skip renewal
          </Button>
        </ModalActions>
      </Modal>
    </PageContainer>
  );
};
